import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Form, Input, Label, Link, Separator } from 'vendor/heroui';
import { display } from 'state/modules/dialog';
import usePush from 'hooks/router/usePush';
import usePasskey from 'hooks/Auth/usePasskey';
import { login } from 'state/modules/auth';
import SingleSignOn from './SingleSignOn';

const inputClassName = [
  'h-12 rounded-xl border border-[var(--owner-border)] bg-[var(--owner-surface)] px-4 text-base text-[var(--owner-text)] shadow-sm transition',
].join(' ');

// Soften HeroUI's data-focused state: stop the dark border-color shift on focus
// and replace the saturated focus ring with a faint tenant-tinted glow.
const softFocusStyle = {
  '--color-field-border-focus': 'var(--owner-border)',
  '--color-focus': 'color-mix(in srgb, var(--tenant-primary) 22%, transparent)',
};

const Login = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { push } = usePush();
  const authStore = useSelector(state => state.auth);
  const tenantId = useSelector(state => state.tenant.id);
  const tenantTheme = useSelector(state => state.tenant.theme);
  const { error } = authStore;
  const { authenticateWithAutofill } = usePasskey();

  // WebAuthn Conditional UI: registers a passive listener tied to the
  // username field's `autocomplete="... webauthn"` attribute below. The
  // browser shows a passkey suggestion inline in its own autofill dropdown
  // (next to saved passwords) whenever one is available, and picking it *is*
  // the user gesture - so unlike the explicit ceremony this doesn't need (and
  // must not wait for) a prior click, and needs no availability check first;
  // the browser simply shows nothing if there's no passkey to offer.
  useEffect(() => {
    authenticateWithAutofill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = event => {
    event.preventDefault();
    const user = document.getElementById('login-user');
    const pass = document.getElementById('login-pass');
    const username = user.value.trim().toLowerCase();
    const password = pass.value.trim();

    if (username === '') {
      dispatch(display(
        formatMessage({ id: 'page.login.message.informationRequired' }),
        formatMessage({ id: 'page.login.message.usernameRequired' }),
        'warning'
      ));
    }
    else if (password === '') {
      dispatch(display(
        formatMessage({ id: 'page.login.message.informationRequired' }),
        formatMessage({ id: 'page.login.message.passwordRequired' }),
        'warning'
      ));
    }
    else {
      dispatch(login(username, password, tenantId));
      // Clearing the password field everytime after submit
      pass.value = '';
    }
  };

  const handleKeyDown = event => {
    if (event.keyCode === 13) {
      handleSubmit(event);
    }
  };

  return (
    <Form className="mx-auto flex w-full max-w-sm flex-col gap-4" onSubmit={handleSubmit}>
      {
        error && error.message && (
          <Alert status="danger">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{formatMessage({ id: error.message, defaultMessage: error.message })}</Alert.Title>
            </Alert.Content>
          </Alert>
        )
      }
      <div className="flex flex-col gap-2">
        <Label htmlFor="login-user" className="text-sm font-semibold text-[var(--owner-text)]">
          <FormattedMessage id="page.login.label.username" />
        </Label>
        <Input
          fullWidth
          id="login-user"
          name="username"
          type="text"
          variant="secondary"
          className={inputClassName}
          style={softFocusStyle}
          placeholder={formatMessage({ id: 'page.login.label.username' })}
          onKeyDown={handleKeyDown}
          autoCapitalize="none"
          autoComplete="username webauthn"
          autoCorrect="off"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="login-pass" className="text-sm font-semibold text-[var(--owner-text)]">
          <FormattedMessage id="page.login.label.password" />
        </Label>
        <Input
          fullWidth
          id="login-pass"
          name="password"
          type="password"
          variant="secondary"
          className={inputClassName}
          style={softFocusStyle}
          placeholder={formatMessage({ id: 'page.login.label.password' })}
          onKeyDown={handleKeyDown}
          autoComplete="current-password"
        />
      </div>
      <Button
        fullWidth
        size="lg"
        type="submit"
        className="mt-2 h-12 rounded-xl font-semibold shadow-sm transition"
        style={tenantTheme.button}
      >
        <FormattedMessage id="page.login.action.login" />
      </Button>
      <Link
        className="mx-auto cursor-pointer text-sm font-semibold"
        onPress={() => push('/forgotPassword')}
        style={tenantTheme.a}
      >
        {formatMessage({ id: 'page.login.label.forgotPassword' })}
      </Link>
      <div className="flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.24em] text-[var(--owner-text-muted)]">
        <Separator className="flex-1" />
        <span className="shrink-0 lowercase tracking-normal">
          <FormattedMessage id="page.login.divider" />
        </span>
        <Separator className="flex-1" />
      </div>
      <SingleSignOn />
    </Form>
  );
};

export default Login;
